import { useState, useEffect } from 'react';
import type { CategoryNodeWithCount } from '@/api/library';

interface SidebarProps {
  activeCategory: string;
  onCategoryChange: (cat: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  categoryTree: CategoryNodeWithCount[];
}

export default function Sidebar({ activeCategory, onCategoryChange, sortBy, onSortChange, categoryTree }: SidebarProps) {
  // 默认展开所有一级分类
  const [expandedPrimary, setExpandedPrimary] = useState<string[]>([]);
  // 默认折叠所有二级分类（有型号的）
  const [expandedSub, setExpandedSub] = useState<string[]>([]);

  // 当分类树加载完成后，自动展开所有一级分类
  useEffect(() => {
    if (categoryTree.length > 0 && expandedPrimary.length === 0) {
      setExpandedPrimary(categoryTree.map((c) => c.id));
    }
  }, [categoryTree, expandedPrimary.length]);

  const togglePrimary = (id: string) => {
    setExpandedPrimary((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSub = (id: string) => {
    setExpandedSub((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // 汇总全部资料数量
  const totalCount = categoryTree.reduce((acc, p) => acc + (p.count || 0), 0);

  // 判断当前激活的是否是某个型号
  const isModelActive = (modelId: string) => activeCategory === `model:${modelId}`;
  // 判断某个二级分类下是否有激活的型号
  const hasActiveModel = (subId: string) => {
    for (const primary of categoryTree) {
      const sub = primary.subCategories.find((s) => s.id === subId);
      if (sub?.models.some((m) => isModelActive(m.id))) return true;
    }
    return false;
  };

  if (categoryTree.length === 0) {
    return (
      <aside className="w-64 shrink-0 hidden lg:block">
        <div className="sticky top-20 space-y-6">
          <div className="animate-pulse space-y-3 px-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-8 bg-gray-100 rounded-lg" style={{ width: `${80 - i * 8}%` }}></div>
            ))}
          </div>
        </div>
      </aside>
    );
  }

  return (
    <aside className="w-64 shrink-0 hidden lg:block">
      <div className="sticky top-20 space-y-6">
        {/* Categories */}
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">Categories</p>
          <nav className="space-y-0.5">
            {/* 全部 */}
            <button
              onClick={() => onCategoryChange('all')}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                activeCategory === 'all' ? 'font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
              style={activeCategory === 'all' ? { backgroundColor: '#0069BD14', color: '#0069BD' } : {}}
            >
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center shrink-0">
                  <i className="ri-folder-3-line text-xs"></i>
                </span>
                <span>All Materials</span>
              </span>
              <span
                className="text-xs rounded-full px-1.5 py-0.5 font-medium shrink-0 ml-2"
                style={activeCategory === 'all' ? { backgroundColor: '#0069BD20', color: '#0069BD' } : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
              >
                {totalCount}
              </span>
            </button>

            {/* 一级分类分组 */}
            {categoryTree.map((primary) => {
              const isExpanded = expandedPrimary.includes(primary.id);
              const anySubActive = primary.subCategories.some(
                (s) => activeCategory === s.id || s.models.some((m) => isModelActive(m.id))
              );

              return (
                <div key={primary.id}>
                  {/* 一级分类标题行 */}
                  <button
                    onClick={() => togglePrimary(primary.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all cursor-pointer mt-1 ${
                      anySubActive ? '' : 'text-gray-500 hover:bg-gray-50'
                    }`}
                    style={anySubActive ? { color: primary.color } : {}}
                  >
                    <span className="flex items-center gap-2">
                      <span
                        className="w-4 h-4 flex items-center justify-center shrink-0 rounded"
                        style={{ background: `${primary.color}18` }}
                      >
                        <i className={`${primary.icon} text-xs`} style={{ color: primary.color }}></i>
                      </span>
                      <span>{primary.label}</span>
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-xs rounded-full px-1.5 py-0.5 font-medium bg-gray-100 text-gray-400">
                        {primary.count || 0}
                      </span>
                      <i className={`text-gray-400 text-xs transition-transform ${isExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                    </span>
                  </button>

                  {/* 二级分类列表 */}
                  {isExpanded && (
                    <div className="ml-3 pl-3 border-l border-gray-100 space-y-0.5 mb-1">
                      {primary.subCategories.map((sub) => {
                        const isSubActive = activeCategory === sub.id;
                        const hasModels = sub.models.length > 0;
                        const isSubExpanded = expandedSub.includes(sub.id);
                        const subHasActiveModel = hasActiveModel(sub.id);
                        const subIsHighlighted = isSubActive || subHasActiveModel;

                        return (
                          <div key={sub.id}>
                            {/* 二级分类行 */}
                            <div className="flex items-center gap-0.5">
                              <button
                                onClick={() => onCategoryChange(sub.id)}
                                className={`flex-1 flex items-center justify-between px-2.5 py-2 rounded-lg text-sm transition-all cursor-pointer ${
                                  subIsHighlighted ? 'font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                                style={subIsHighlighted ? { backgroundColor: `${primary.color}12`, color: primary.color } : {}}
                              >
                                <span className="flex items-center gap-2 truncate">
                                  <span className="w-3.5 h-3.5 flex items-center justify-center shrink-0">
                                    <i className={`${sub.icon} text-xs`} style={{ color: subIsHighlighted ? primary.color : '#9ca3af' }}></i>
                                  </span>
                                  <span className="truncate">{sub.label}</span>
                                </span>
                                <span
                                  className="text-xs rounded-full px-1.5 py-0.5 font-medium shrink-0 ml-1"
                                  style={subIsHighlighted ? { backgroundColor: `${primary.color}20`, color: primary.color } : { backgroundColor: '#f3f4f6', color: '#6b7280' }}
                                >
                                  {sub.count || 0}
                                </span>
                              </button>
                              {/* 展开/折叠型号按钮 */}
                              {hasModels && (
                                <button
                                  onClick={() => toggleSub(sub.id)}
                                  className="w-6 h-6 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors cursor-pointer shrink-0"
                                  title={isSubExpanded ? 'Collapse' : 'Expand'}
                                >
                                  <i className={`text-xs transition-transform ${isSubExpanded ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`}></i>
                                </button>
                              )}
                            </div>

                            {/* 三级分类（产品型号）列表 */}
                            {hasModels && isSubExpanded && (
                              <div className="ml-3 pl-3 border-l border-gray-100 space-y-0.5 mt-0.5 mb-1">
                                {sub.models.map((model) => {
                                  const modelActive = isModelActive(model.id);
                                  return (
                                    <button
                                      key={model.id}
                                      onClick={() => onCategoryChange(`model:${model.id}`)}
                                      className={`w-full flex items-center justify-between px-2 py-1.5 rounded-md text-xs transition-all cursor-pointer ${
                                        modelActive ? 'font-semibold' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                                      }`}
                                      style={modelActive ? { backgroundColor: `${primary.color}10`, color: primary.color } : {}}
                                    >
                                      <span className="flex items-center gap-1.5 truncate">
                                        <span
                                          className="w-1.5 h-1.5 rounded-full shrink-0"
                                          style={{ background: modelActive ? primary.color : '#d1d5db' }}
                                        ></span>
                                        <span className="truncate">{model.label}</span>
                                      </span>
                                      {(model.count || 0) > 0 && (
                                        <span
                                          className="text-xs rounded-full px-1 py-0.5 font-medium shrink-0 ml-1"
                                          style={modelActive ? { backgroundColor: `${primary.color}20`, color: primary.color } : { backgroundColor: '#f3f4f6', color: '#9ca3af' }}
                                        >
                                          {model.count || 0}
                                        </span>
                                      )}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        </div>

        {/* Sort */}
        <div>
          <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">Sort By</p>
          <div className="space-y-0.5">
            {[
              { value: 'latest', label: 'Latest Upload', icon: 'ri-time-line' },
              { value: 'popular', label: 'Most Popular', icon: 'ri-fire-line' },
              { value: 'az', label: 'A → Z', icon: 'ri-sort-asc' },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => onSortChange(opt.value)}
                className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all cursor-pointer whitespace-nowrap ${
                  sortBy === opt.value ? 'font-semibold' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
                style={sortBy === opt.value ? { backgroundColor: '#0069BD14', color: '#0069BD' } : {}}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${opt.icon} text-sm`}></i>
                </div>
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </aside>
  );
}
